import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ampli } from '../../../../ampli';
import { pageInfo } from '../../../../constants/pageInfo/pageInfo.constant';
import { useGetUserSellTractorSearchData } from '../../../../hooks/api/userSellTractor/queries/useGetUserSellTractorSearchData.query';

interface IRecommendedSearchKeywordProps {
  searchKeyword: string;
}

export const RecommendedSearchKeyword = ({ searchKeyword }: IRecommendedSearchKeywordProps) => {
  const navigate = useNavigate();

  const { data } = useGetUserSellTractorSearchData(
    {
      q: searchKeyword.trim(),
      limit: 1000, // 추천 검색어는 모든 결과를 가져오기 위해 큰 limit 설정
    },
    {
      enabled: Boolean(searchKeyword.trim()),
    },
  );

  const searchedProducts = data?.hits;

  // 필터링 조건:
  // 1. Left Image(modelImage)가 있어야 함
  // 2. 마력(engineOutputInPs) 또는 조식(rowConfig) 중 하나라도 있어야 함
  // 3. 판매자(seller) 정보가 있어야 함
  const filteredProducts = searchedProducts
    ? searchedProducts.filter((product) => {
        const hasImage = Boolean(product.modelImage);
        const hasPowerOrRow = Boolean(
          product.engineOutputInPs ||
            product.machine?.engineOutputInPs ||
            product.rowConfig ||
            product.machine?.rowConfig,
        );
        const hasSeller = Boolean(product.seller?.id);

        return hasImage && hasPowerOrRow && hasSeller;
      })
    : [];

  // 브랜드+모델 조합이 중복되지 않도록 필터링
  const uniqueProducts = filteredProducts.reduce(
    (acc, product) => {
      const key = `${product.brand?.name}_${product.machine?.model}`;

      // 이미 같은 브랜드+모델 조합이 있는지 확인
      const exists = acc.some((item) => `${item.brand?.name}_${item.machine?.model}` === key);

      // 없으면 추가
      if (!exists) {
        acc.push(product);
      }

      return acc;
    },
    [] as typeof filteredProducts,
  );

  const renderHighlightedText = (text: string) => {
    if (!searchKeyword) return text;

    const trimmedSearchKeyword = searchKeyword.trim();

    const index = text.toLowerCase().indexOf(trimmedSearchKeyword.toLowerCase());
    if (index === -1) return text;

    const beforeMatch = text.slice(0, index);
    const match = text.slice(index, index + trimmedSearchKeyword.length);
    const afterMatch = text.slice(index + trimmedSearchKeyword.length);

    return (
      <>
        {beforeMatch}
        <MatchedText>{match}</MatchedText>
        {afterMatch}
      </>
    );
  };

  if (uniqueProducts && uniqueProducts.length !== 0) {
    return (
      <Container>
        {uniqueProducts.map((product) => {
          // Meilisearch 데이터 구조: brand.name + machine.model
          const displayText = `${product.brand?.name} ${product.machine?.model}`;

          return (
            <RecommendedKeywordWrapper
              key={product.id}
              onClick={() => {
                ampli.pvmClickOpenMarketSearchAutoCompleteKeyword({
                  title: displayText,
                  typed_keyword: searchKeyword.trim(),
                });
                navigate(`${pageInfo.openMarketSearchResultPage.path}?keyword=${displayText}`, {
                  replace: true,
                });
              }}
            >
              <RecommendedKeyword>{renderHighlightedText(displayText)}</RecommendedKeyword>
              <SearchButton>
                <SearchButtonText>{'검색'}</SearchButtonText>
              </SearchButton>
            </RecommendedKeywordWrapper>
          );
        })}
      </Container>
    );
  }

  return null;
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: stretch;

  padding: 0px 16px;
`;

const RecommendedKeywordWrapper = styled.div`
  display: flex;
  align-items: center;
  align-self: stretch;

  padding: 16px 0px;

  border-bottom: 1px solid var(--Light-Scheme-Monochrome-grey10, #f2f2f7);

  cursor: pointer;
`;

const RecommendedKeyword = styled.span`
  flex: 1 0 0;

  color: var(--Light-Scheme-Monochrome-grey100, #858894);

  /* Body/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`;

const MatchedText = styled(RecommendedKeyword)`
  color: var(--Light-Scheme-Monochrome-black, #232429);
`;

const SearchButton = styled.button`
  height: 22px;

  display: flex;
  align-items: center;
  gap: 2px;

  padding: 6px 8px;

  border-radius: var(--radius-round, 80px);
  background: var(--Light-Scheme-Monochrome-grey20, #ececf3);
`;

const SearchButtonText = styled.span`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);
  text-align: center;

  /* System/Small/s-medium */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 10.2px */
`;
